import { useEffect, useRef } from 'react';
import { subscribe, type Subscription } from './lib/aggSubscription';
import { type ProductContextKey } from './lib/realtime';
import { validateRealtimeChannelToken } from './lib/realtimeToken';
import { fg } from '@atlaskit/platform-feature-flags';
import type { GetContextTokenFn, GetContextTokenOptions } from '../../custom-ui/iframe/types';

const realtimeChannel = `
	subscription realtimeChannel(
		$installationId: ID!
		$name: String!
		$context: String
		$isGlobal: Boolean
		$token: String
	) {
		ecosystem {
			realtimeChannel(
				installationId: $installationId
				name: $name
				context: $context
				isGlobal: $isGlobal
				token: $token
			) {
				payload
			}
		}
	}
`;

export type SubscriptionCallback = (payload: unknown, error?: Error) => void;

export type SubscribeRealtimeChannelPayload = {
	data: {
		channelName: string;
		isGlobal?: boolean;
		onEvent: SubscriptionCallback;
		options?: {
			contextOverrides?: ProductContextKey[];
			replaySeconds?: number;
			token?: string;
		};
	};
};

export type SubscribeRealtimeChannel = (
	payload: SubscribeRealtimeChannelPayload,
	contextToken?: string,
) => Promise<{ unsubscribe: () => void } | undefined>;

type SubscriptionResult = {
	data: {
		ecosystem: {
			globalRealtimeChannel?: {
				payload?: string;
			};
			realtimeChannel?: {
				payload?: string;
			};
		};
	};
};

const getChannelKey = (context: {
	channelContext?: string;
	channelName: string;
	isGlobal?: boolean;
}) => {
	return Object.entries(context)
		.sort((a, b) => a[0].localeCompare(b[0]))
		.toString();
};

/**
 * Form the subscribe payload for the realtime channel.
 *
 * @param payload - Contains all app developer-provided data/arguments
 * @param context - The context that determines the scope of the realtime channel.
 * For example - if Jira issue info { issue: { id: 'my-issue-123 } } is provided, realtime messages are only sent/received by clients that have access to that issue.
 * @param installationId - The installation id of the app
 * @param contextToken - The Forge Context Token (FCT) used to validate that the context has not been tampered with
 */
export const getSubscribePayload = ({
	payload,
	context,
	installationId,
	contextToken,
}: {
	context: string;
	contextToken?: string;
	installationId: string;
	payload: SubscribeRealtimeChannelPayload;
}): {
	extensions: {
		command?:
			| {
					data: {
						// Timestamp in seconds of when to replay messages from. Valid for up to 7 days in the past.
						sequenceNumber: number;
					};
					type: string;
			  }
			| undefined;
		'x-forge-context-token': string | undefined;
	};
	operationName: string;
	query: string;
	variables: {
		context?: string | undefined;
		installationId: string;
		isGlobal?: boolean | undefined;
		name: string;
		token: string | undefined;
	};
} => {
	const query = {
		operationName: 'realtimeChannel',
		query: realtimeChannel,
	};

	return {
		...query,
		variables: {
			installationId,
			name: payload.data.channelName,
			context,
			isGlobal: payload.data.isGlobal ?? false,
			token: payload.data.options?.token,
		},
		extensions: {
			'x-forge-context-token': contextToken,
			...(payload.data.options?.replaySeconds && {
				command: {
					type: 'replay',
					data: {
						// Timestamp in seconds of when to replay messages from. Valid for up to 7 days in the past.
						sequenceNumber: Date.now() / 1000 - payload.data.options.replaySeconds,
					},
				},
			}),
		},
	};
};

const unsubscribeAllSubscriptions = (activeSubscriptions: Map<string, Subscription>) => {
	activeSubscriptions.forEach((subscription) => {
		if (!subscription.closed) {
			subscription.unsubscribe();
		}
	});
	activeSubscriptions.clear();
};

/**
 * This hook is used to subscribe to client events from the extension.
 * It will return a function that can be called to unsubscribe from the client events.
 *
 * This hook has no side effect (i.e. does not actively trigger any network calls or actively making any subscription.
 * It is because everything inside this hook is lazily initialized.
 * Importantly:
 * - It only has impact when the subscribeClientEvent is called by the client (e.g. @forge/bridge)
 * - The backend service (AGG subscription) is feature flagged and is not currently live in production, hence calling this hook in production or without
 *   enabled flag will have no impact.
 *
 * @param installationId The installation id
 * @param context The context string for the realtime channel
 * @param getContextToken Optional function to retrieve Forge Context Tokens
 * @param getContextTokenOptions Optional options for getContextToken (e.g., extensionOverride for embedded macros)
 * @returns The subscribeClientEvent function that can be called to subscribe to client events
 */
export const useSubscribeRealtimeChannel = (
	installationId: string,
	context: string,
	getContextToken?: GetContextTokenFn,
	getContextTokenOptions?: GetContextTokenOptions,
) => {
	const activeSubscriptions = useRef<Map<string, Subscription>>(new Map());

	useEffect(() => {
		const currentSubscriptions = activeSubscriptions.current;
		return () => {
			unsubscribeAllSubscriptions(currentSubscriptions);
		};
	}, []);

	// this is the bridge method that will be called from the extension
	return async (
		payload: SubscribeRealtimeChannelPayload,
	): Promise<{
		unsubscribe: () => void;
	}> => {
		const { onEvent, channelName, options, isGlobal } = payload.data;
		const { token } = options || {};

		const prevalidate =
			fg('forge-ui-realtime-add-pre-permissions-validation') ||
			fg('forge-ui-realtime-pre-validate-frt-existing-fields');

		if (token && prevalidate) {
			const validation = validateRealtimeChannelToken(token, {
				requiredPermission: 'subscribe',
				channelName,
			});

			if (!validation.valid) {
				return Promise.reject(new Error(`Realtime token validation failed: ${validation.error}`));
			}
		}

		const channelContext = options?.contextOverrides
			? JSON.stringify({ context, contextOverrides: options?.contextOverrides })
			: context;

		const channelKey = getChannelKey({
			channelName,
			isGlobal,
			channelContext,
		});

		const existingSubscription = activeSubscriptions.current.get(channelKey);

		if (existingSubscription) {
			// if subscription already exists
			existingSubscription.addCallback(onEvent);
			onEvent('SUBSCRIPTION_STARTED');
		} else {
			// helper to create a subscription and handle token refresh
			const createSubscription = async () => {
				const subscriptionPayload = getSubscribePayload({
					payload,
					context: channelContext,
					installationId,
					contextToken: await getContextToken?.(getContextTokenOptions),
				});

				const subscription = subscribe(
					subscriptionPayload,
					{
						next: (result: SubscriptionResult) => {
							subscription.onNext(result.data.ecosystem?.realtimeChannel?.payload);
						},
						error: async (err: unknown) => {
							const error = Array.isArray(err) ? err[0] : err;

							const shouldRetrySubscribe = fg('forge-ui-retry-subscribe-only-for-expired-fct')
								? error?.message === 'FCT has expired on subscribe request'
								: error?.message === 'Unauthorized request' ||
									error?.message === 'FCT has expired on subscribe request';

							if (shouldRetrySubscribe) {
								try {
									const newSubscription = await createSubscription();

									newSubscription.subscriptionCallbacks = [...subscription.subscriptionCallbacks];
									activeSubscriptions.current.set(channelKey, newSubscription);
									subscription.unsubscribe();
									return;
								} catch (err) {
									subscription.onError(err as Error);
									activeSubscriptions.current.delete(channelKey);
									return;
								}
							}

							subscription.onError(err as Error);
							activeSubscriptions.current.delete(channelKey);
						},
						complete: () => {
							activeSubscriptions.current.delete(channelKey);
						},
					},
					onEvent,
				);

				activeSubscriptions.current.set(channelKey, subscription);
				return subscription;
			};

			createSubscription();
		}

		return {
			unsubscribe: () => {
				const subscription = activeSubscriptions.current.get(channelKey);
				if (subscription) {
					// remove one subscription
					subscription.removeCallback(onEvent);

					// if no more subscriptions remove the channelKey and unsubscribe
					if (subscription.subscriptionCallbacks.length === 0) {
						activeSubscriptions.current.delete(channelKey);
						if (!subscription.closed) {
							subscription.unsubscribe();
						}
					}
				}
			},
		};
	};
};
